/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { isEmpty, withErrorBoundary } from '@navi-app/utils';
import { mergeOverrides } from 'baseui';
import { FormControl } from 'baseui/form-control';
import { Input, InputProps, StyledInput } from 'baseui/input';
import { useStyletron } from 'baseui/styles';
import { Tag } from 'baseui/tag';

import { ParagraphXSmall } from 'baseui/typography';
import React from 'react';
import { Toaster } from '..';

const InputReplacement = React.forwardRef(
  ({ tags, removeTag, ...restProps }: any, ref) => {
    const [css] = useStyletron();
    return (
      <div
        className={css({
          flex: '1 1 0%',
          flexWrap: 'wrap',
          display: 'flex',
          alignItems: 'center',
        })}
      >
        {tags.map((tag: string, index: number) => (
          <Tag
            onActionClick={() => removeTag(tag)}
            key={index}
            overrides={{
              Root: {
                style: {
                  backgroundColor: '#727272',
                  borderRadius: '4px',
                  border: '#5E5E5E',
                  color: '#F3F3F3',
                  padding: '4px 6px',
                },
              },
              ActionIcon: {
                component: () => {
                  return <XMarkIcon className="w-3 h-3" color="#F3F3F3" />;
                },
              },
            }}
          >
            {tag}
          </Tag>
        ))}
        <StyledInput ref={ref} {...restProps} />
      </div>
    );
  }
);

type TPropsInput = {
  errorMessageWithIcon?: string;
  id?: string;
  value?: string;
  error?: React.ReactNode | ((props: {}) => React.ReactNode) | null;
  disabled?: boolean;
  label?: React.ReactNode | ((props: {}) => React.ReactNode) | undefined | null;
  caption?:
    | React.ReactNode
    | ((props: {}) => React.ReactNode)
    | undefined
    | null;
  isInputOnly?: boolean;
  placeholder?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  tags: string[];
  setTags: (tags: string[]) => void;
  tagsInfo?: string | React.ReactNode;
  tagsLimit?: number;
  onEnterSubmit?: () => void;
} & InputProps;

const InputTags = ({
  id,
  value,
  error,
  disabled,
  label,
  caption,
  size,
  isInputOnly = false,
  placeholder,
  onChange,
  errorMessageWithIcon,
  tags,
  setTags,
  tagsInfo,
  tagsLimit = 20,
  onEnterSubmit,
  overrides,
  ...rest
}: TPropsInput) => {
  const addTag = (tag: string) => {
    if (tags.includes(tag)) return;
    setTags([...tags, tag]);
  };
  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.keyCode) {
      // Enter
      case 13: {
        if (!value) {
          onEnterSubmit?.();
          return;
        }
        if (tags.length >= tagsLimit) {
          Toaster.show(`Maksimal ${tagsLimit} tags`);
          return;
        }
        addTag(value);
        onChange?.({
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>);
        return;
      }
      // Backspace
      case 8: {
        if (value || !tags.length) return;
        removeTag(tags[tags.length - 1]);
        return;
      }
    }
  };

  const ErrorMessage = () => {
    return errorMessageWithIcon ? (
      <div className="flex items-center -mt-1">
        <ExclamationCircleIcon className="w-4 h-4 mr-1 mt-1" color="#DE1135" />
        <ParagraphXSmall color="#DE1135">
          {errorMessageWithIcon}
        </ParagraphXSmall>
      </div>
    ) : null;
  };
  return (
    <FormControl
      label={label}
      caption={caption}
      error={error}
      disabled={disabled}
      overrides={{
        ControlContainer: {
          style: isInputOnly
            ? {
                marginBottom: 0,
              }
            : {},
        },
      }}
    >
      <div>
        <Input
          {...rest}
          id={id}
          value={value}
          placeholder={isEmpty(tags) ? placeholder : undefined}
          error={!!error}
          onChange={onChange}
          disabled={disabled}
          overrides={mergeOverrides(
            {
              Input: {
                style: { width: 'auto', flexGrow: 1 },
                component: InputReplacement,
                props: {
                  tags: tags,
                  removeTag: removeTag,
                  onKeyDown: handleKeyDown,
                },
              },
            },
            overrides ?? {}
          )}
        />
        <div className="mt-2">
          <ErrorMessage />
        </div>
        <ParagraphXSmall className="mt-1" color="#5E5E5E">
          {tagsInfo}
        </ParagraphXSmall>
      </div>
    </FormControl>
  );
};

export default withErrorBoundary(InputTags);
